import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import {
  Braces,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  Eye,
  Loader2,
  MousePointerClick,
  RefreshCw,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  formatEngagementWhen,
  loadNewsletterEngagementEvents,
  type NewsletterEngagementEventRow,
} from '../../lib/emailEngagement';
import NewsletterTrackingHelp from './NewsletterTrackingHelp';

function subscriberLabel(send: NewsletterEngagementEventRow['scheduled_emails']): string {
  const sub = send?.subscribers;
  const email = sub?.email?.trim();
  if (email) return email;
  const name = sub?.name?.trim();
  if (name) return name;
  if (send?.subscriber_id) return send.subscriber_id;
  return '—';
}

function eventJson(r: NewsletterEngagementEventRow): string {
  return JSON.stringify(
    {
      id: r.id,
      event_type: r.event_type,
      occurred_at: r.occurred_at,
      destination_url: r.destination_url,
      scheduled_email_id: r.scheduled_email_id,
      subscriber_id: r.scheduled_emails?.subscriber_id ?? null,
    },
    null,
    2,
  );
}

type TrackingSortKey = 'when' | 'subscriber' | 'event' | 'link';

type TrackingSortConfig = {
  column: TrackingSortKey;
  direction: 'asc' | 'desc';
};

const DEFAULT_SORT: TrackingSortConfig = { column: 'when', direction: 'desc' };

function compareSortValues(
  a: string | number | null,
  b: string | number | null,
  direction: 'asc' | 'desc',
): number {
  const emptyA = a == null || a === '';
  const emptyB = b == null || b === '';
  if (emptyA && emptyB) return 0;
  if (emptyA) return direction === 'asc' ? 1 : -1;
  if (emptyB) return direction === 'asc' ? -1 : 1;
  if (typeof a === 'number' && typeof b === 'number') {
    const cmp = a - b;
    return direction === 'asc' ? cmp : -cmp;
  }
  const cmp = String(a).localeCompare(String(b), undefined, { sensitivity: 'base' });
  return direction === 'asc' ? cmp : -cmp;
}

function sortValueForRow(row: NewsletterEngagementEventRow, column: TrackingSortKey): string | null {
  switch (column) {
    case 'when':
      return row.occurred_at;
    case 'subscriber':
      return subscriberLabel(row.scheduled_emails).toLowerCase();
    case 'event':
      return row.event_type;
    case 'link':
      return row.destination_url?.trim() || null;
    default:
      return null;
  }
}

function defaultSortDirection(column: TrackingSortKey): 'asc' | 'desc' {
  return column === 'when' ? 'desc' : 'asc';
}

function SortableTh({
  label,
  column,
  sortConfig,
  onSort,
  className = '',
}: {
  label: string;
  column: TrackingSortKey;
  sortConfig: TrackingSortConfig;
  onSort: (column: TrackingSortKey) => void;
  className?: string;
}) {
  const active = sortConfig.column === column;
  const Icon = active && sortConfig.direction === 'desc' ? ChevronDown : ChevronUp;
  return (
    <th className={`text-left px-4 py-2 font-medium ${className}`}>
      <button
        type="button"
        onClick={() => onSort(column)}
        className={`inline-flex items-center gap-0.5 uppercase tracking-wide hover:text-gray-700 dark:hover:text-gray-200 ${
          active ? 'text-gray-700 dark:text-gray-200' : ''
        }`}
      >
        {label}
        <Icon className={`w-3.5 h-3.5 ${active ? '' : 'opacity-30'}`} />
      </button>
    </th>
  );
}

interface NewsletterTrackingTabProps {
  newsletterId: string;
  /** Bump when parent refreshes sends / engagement. */
  refreshKey?: number;
}

export default function NewsletterTrackingTab({
  newsletterId,
  refreshKey = 0,
}: NewsletterTrackingTabProps) {
  const [rows, setRows] = useState<NewsletterEngagementEventRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [sortConfig, setSortConfig] = useState<TrackingSortConfig>(DEFAULT_SORT);

  const handleSort = useCallback((column: TrackingSortKey) => {
    setSortConfig((prev) => {
      if (prev.column === column) {
        return { column, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { column, direction: defaultSortDirection(column) };
    });
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRows(await loadNewsletterEngagementEvents(newsletterId));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load tracking events');
    } finally {
      setLoading(false);
    }
  }, [newsletterId]);

  useEffect(() => {
    void load();
  }, [load, refreshKey]);

  const eventStats = useMemo(() => {
    const openSendIds = new Set<string>();
    const clickSendIds = new Set<string>();
    let opens = 0;
    let clicks = 0;
    for (const r of rows) {
      if (r.event_type === 'open') {
        opens += 1;
        if (r.scheduled_email_id) openSendIds.add(r.scheduled_email_id);
      } else if (r.event_type === 'click') {
        clicks += 1;
        if (r.scheduled_email_id) clickSendIds.add(r.scheduled_email_id);
      }
    }
    return {
      total: rows.length,
      opens,
      clicks,
      uniqueOpens: openSendIds.size,
      uniqueClickers: clickSendIds.size,
    };
  }, [rows]);

  const sortedRows = useMemo(() => {
    return [...rows].sort((a, b) =>
      compareSortValues(
        sortValueForRow(a, sortConfig.column),
        sortValueForRow(b, sortConfig.column),
        sortConfig.direction,
      ),
    );
  }, [rows, sortConfig]);

  const totalLabel = useMemo(() => {
    if (rows.length === 0) return null;
    const { total, opens, clicks, uniqueOpens, uniqueClickers } = eventStats;
    return `${total} event${total === 1 ? '' : 's'} · ${opens} open${opens === 1 ? '' : 's'} (${uniqueOpens} unique) · ${clicks} click${clicks === 1 ? '' : 's'} (${uniqueClickers} unique clickers)`;
  }, [rows.length, eventStats]);

  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden">
      <div className="px-5 sm:px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide">
            Postmark tracking
          </h2>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Raw rows from <code className="font-mono">email_engagement_events</code> — one row per
            Postmark open or click webhook. Times are Postmark{' '}
            <span className="font-medium">ReceivedAt</span>, not send time.
          </p>
          {totalLabel ? (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 tabular-nums">{totalLabel}</p>
          ) : null}
        </div>
        <button
          type="button"
          onClick={() => void load()}
          title="Refresh"
          aria-label="Refresh tracking events"
          className="inline-flex items-center justify-center p-2 rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 shrink-0"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {loading && rows.length === 0 ? (
        <div className="min-h-[min(48vh,440px)] p-10 flex items-center justify-center">
          <Loader2 className="w-5 h-5 text-emerald-500 animate-spin" />
        </div>
      ) : error ? (
        <div className="min-h-[min(48vh,440px)] p-6 flex items-center justify-center text-sm text-red-700 dark:text-red-300">
          {error}
        </div>
      ) : rows.length === 0 ? (
        <div className="min-h-[min(48vh,440px)] p-10 flex flex-col items-center justify-center gap-2 text-sm text-gray-500 dark:text-gray-400 text-center">
          <p>No opens or clicks recorded for this newsletter yet.</p>
          <p className="text-xs max-w-md">
            Events appear after sends reach <span className="font-medium">sent</span> and recipients
            open or click tracked links. See the panel below for how Postmark webhooks are stored.
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]">
            <thead className="bg-gray-50 dark:bg-gray-900/40 text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
              <tr>
                <th className="w-8 px-2 py-2" aria-label="Expand" />
                <SortableTh label="When" column="when" sortConfig={sortConfig} onSort={handleSort} />
                <SortableTh label="Event" column="event" sortConfig={sortConfig} onSort={handleSort} />
                <SortableTh
                  label="Subscriber"
                  column="subscriber"
                  sortConfig={sortConfig}
                  onSort={handleSort}
                />
                <th className="text-left px-4 py-2 font-medium">Send</th>
                <SortableTh label="Link" column="link" sortConfig={sortConfig} onSort={handleSort} />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {sortedRows.map((r) => {
                const expanded = expandedId === r.id;
                const send = r.scheduled_emails;
                const subject = send?.subject?.trim();
                return (
                  <Fragment key={r.id}>
                    <tr className="text-gray-800 dark:text-gray-200">
                      <td className="px-2 py-2">
                        <button
                          type="button"
                          onClick={() => setExpandedId(expanded ? null : r.id)}
                          className="p-1 rounded text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700"
                          aria-expanded={expanded}
                          aria-label={expanded ? 'Hide raw event' : 'Show raw event'}
                        >
                          {expanded ? (
                            <ChevronDown className="w-4 h-4" />
                          ) : (
                            <ChevronRight className="w-4 h-4" />
                          )}
                        </button>
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-gray-600 dark:text-gray-300 tabular-nums">
                        {formatEngagementWhen(r.occurred_at)}
                      </td>
                      <td className="px-4 py-2">
                        <span
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                            r.event_type === 'open'
                              ? 'bg-sky-50 text-sky-700 dark:bg-sky-900/30 dark:text-sky-200'
                              : 'bg-violet-50 text-violet-700 dark:bg-violet-900/30 dark:text-violet-200'
                          }`}
                        >
                          {r.event_type === 'open' ? (
                            <Eye className="w-3 h-3" />
                          ) : (
                            <MousePointerClick className="w-3 h-3" />
                          )}
                          {r.event_type === 'open' ? 'Open' : 'Click'}
                        </span>
                      </td>
                      <td className="px-4 py-2 max-w-[200px]">
                        {send?.subscriber_id ? (
                          <Link
                            to={`/subscribers/${send.subscriber_id}`}
                            className="text-emerald-600 dark:text-emerald-400 hover:underline truncate block"
                            title={subscriberLabel(send)}
                          >
                            {subscriberLabel(send)}
                          </Link>
                        ) : (
                          <span className="text-gray-500">—</span>
                        )}
                      </td>
                      <td className="px-4 py-2 max-w-[180px] truncate text-xs font-mono text-gray-600 dark:text-gray-300">
                        {subject || r.scheduled_email_id || '—'}
                      </td>
                      <td className="px-4 py-2 max-w-[240px] truncate text-xs text-gray-600 dark:text-gray-300">
                        {r.event_type === 'click' && r.destination_url ? (
                          <a
                            href={r.destination_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="hover:underline text-emerald-600 dark:text-emerald-400"
                            title={r.destination_url}
                          >
                            {r.destination_url}
                          </a>
                        ) : (
                          ''
                        )}
                      </td>
                    </tr>
                    {expanded ? (
                      <tr className="bg-gray-50/80 dark:bg-gray-900/40">
                        <td colSpan={6} className="px-4 py-3">
                          <div className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
                            <Braces className="w-3.5 h-3.5" />
                            Raw event
                          </div>
                          <pre className="text-xs font-mono text-gray-800 dark:text-gray-200 bg-white dark:bg-gray-950 border border-gray-200 dark:border-gray-700 rounded-lg p-3 overflow-x-auto">
                            {eventJson(r)}
                          </pre>
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <NewsletterTrackingHelp />
    </div>
  );
}
